import {PAYMENT_METHODS} from '@optimaxdev/analytics/desktop/constants/paymentMethods';
import {createEECCheckoutStepEvent} from '@optimaxdev/analytics/desktop/lib/ecommerce/checkout/index';
import {pushDataLayer} from '@optimaxdev/analytics/desktop/lib/pushDataLayer/index';
import {BasicOrderProductType} from '@optimaxdev/analytics/desktop/types/ecommerce';
import {waitForFulfilled, WaitForFulfilledType} from '@optimaxdev/utils';
import {call, select, takeEvery} from 'redux-saga/effects';

import {QUICK_CHECKOUT_INITIALIZED} from 'constants/constants';
import {PRODUCT_TYPES} from 'constants/product';
import {CartItemType} from 'desktop/pages/cart/types';
import {STEPS} from 'desktop/pages/checkout/constants/checkout';

import {PAYMENTS} from 'desktop/pages/checkout/constants/paymentStep';

import {ApplicationStoreType} from 'desktop/store';
import {CheckoutItemsType, CheckoutItemType} from 'desktop/types/checkoutItemsType';

import {apiConfig} from 'libs/api/api';
import {getLatestCartItem} from 'libs/getLatestCartItem/getLatestCartItem';
import {localStore} from 'libs/storage/storage';

import {setOptionPaymentMethod} from '../../../reducers/analytics/analytics';
import {setCheckoutStep} from '../../../reducers/currentStep/currentStep';
import {getOrderReviewItems} from '../../../reducers/orderReviewItems/orderReviewItems';
import {getCurrentStep} from '../../../selectors/currentStep/currentStep';
import {
    getOrderReviewItems as orderReviewItemsSelector,
    getIsOrderReviewItemsLoaded,
} from '../../../selectors/orderReviewItems/orderReviewItems';
import {getNameSelectedMethod} from '../../../selectors/paymentStep/paymentStep';
import {ONE_DAY_DELIVERY_LABEL, TWO_DAYS_DELIVERY_LABEL} from '../../shipping/__helpers/constants';

import {getAdditionalFields} from './additionalFields';
import {
    getVariantByCheckoutItem,
    prepareCategory,
    getBrandName,
    getItemPrice,
} from './checkoutOptionHelpers';

export const STEP: {[key: string]: number} = {
    [STEPS.DELIVERY_STEP]: 1,
    [STEPS.PAYMENT_STEP]: 2,
};

/**
 * Get subscription for contact lenses product
 * @param {boolean} subscriptionMode - is subscription enabled
 * @param {string} productType - product type
 * @returns {string} - subscribed or not
 */
const getSubscriptionForLenses = (productType: string, subscriptionMode: boolean): string =>
    productType === PRODUCT_TYPES.CONTACT_LENSES && subscriptionMode ? 'Subscription' : '';

/**
 * Preparing data for sending the checkoutOption event
 * @param {number} step - step number
 * @param {string} option - additional fields
 */
export const prepareDataForPush = (step: number, option: string) => {
    pushDataLayer({
        ecommerce: {
            checkout_option: {
                actionField: {
                    action: 'checkout_option',
                    option: option === PAYMENTS.METHOD_PAYPAL_V3 ? PAYMENT_METHODS[option] : option,
                    step,
                },
            },
        },
        event: 'checkoutOption',
    });
};

/**
 * Get payment method.
 *
 * @param {string} paymentMethod - selected payment method.
 * @returns {string} - Payment method.
 */
const getPaymentMethod = (paymentMethod: string): string => {
    if (paymentMethod === PAYMENTS.METHOD_GIFT) return 'giftcert';
    return paymentMethod;
};
/**
 * Send payment step info to dataLayer
 */
export function* sendPaymentStep() {
    const paymentMethod: string = yield select(getNameSelectedMethod);
    const code: string = yield call(getPaymentMethod, paymentMethod);
    const checkedOption = code === TWO_DAYS_DELIVERY_LABEL ? ONE_DAY_DELIVERY_LABEL : code;
    yield call(prepareDataForPush, STEP[STEPS.PAYMENT_STEP], checkedOption);
}

/**
 * Send eec checkout step event to dataLayer
 * @param {string} step - current step
 * @param {CheckoutItemsType} items  - order items
 */
export function* sendEECCheckoutStepEvent(step: string, items: CheckoutItemsType) {
    const eecCheckoutStepEventBase: {
        step: number;
        products: BasicOrderProductType[];
        availablePaymentMethods?: string;
        autoAppliedAddress?: string;
    } = {
        products: items.map((item: CheckoutItemType): BasicOrderProductType => {
            const {product, qty: quantity, sku, subscription} = item;
            return {
                brand: getBrandName(product),
                category: prepareCategory(item),
                dimension21: sku,
                dimension82: getSubscriptionForLenses(
                    item.product.type,
                    Boolean(subscription?.subscriptionMode),
                ),
                id: product.sku || sku,
                name: item.windowName || item.product.name || '',
                price: getItemPrice(item),
                quantity,
                variant: getVariantByCheckoutItem(product),
            };
        }),
        step: STEP[step],
    };
    const additionalField: {
        availablePaymentMethods?: string;
        autoAppliedAddress?: string;
    } = yield getAdditionalFields(step);
    yield call(
        pushDataLayer,
        createEECCheckoutStepEvent({
            ...eecCheckoutStepEventBase,
            ...additionalField,
        }),
    );
}
/**
 * Send checkout step event to dataLayer
 */
export function* sendCheckoutStep() {
    const {location} = apiConfig.hostStore.getState().router;
    const isCheckout: boolean = ['/checkout/onepage/', '/checkout/onepage'].includes(
        location.pathname,
    );
    if (!isCheckout) return;

    yield call<WaitForFulfilledType<ApplicationStoreType>>(
        waitForFulfilled,
        getIsOrderReviewItemsLoaded,
        `${getOrderReviewItems.fulfilled}`,
    );
    const step: string = yield select(getCurrentStep);
    const items: CheckoutItemsType = yield select(orderReviewItemsSelector);
    const isQuickCheckout: boolean = yield call([localStore, 'get'], QUICK_CHECKOUT_INITIALIZED);
    const finalItems = isQuickCheckout
        ? getLatestCartItem<CartItemType | CheckoutItemType>(items)
        : items;
    yield call(sendEECCheckoutStepEvent, step, finalItems as CheckoutItemsType);
}

/**
 * Checkout option saga
 */
export default function* checkoutOptionSaga() {
    yield takeEvery(`${setCheckoutStep}`, sendCheckoutStep);
    yield takeEvery(`${setOptionPaymentMethod}`, sendPaymentStep);
}
